import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { ListChecks, Plus, Wine, Package, Trash2 } from "lucide-react";

const STATUS_CONFIG = {
  pending_approval: { label: "In attesa", color: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Approvato", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rifiutato", color: "bg-red-100 text-red-800 border-red-200" },
  sold: { label: "Venduto", color: "bg-blue-100 text-blue-800 border-blue-200" },
  withdrawn: { label: "Ritirato", color: "bg-gray-100 text-gray-700 border-gray-200" },
};

export default function B2BMyLots() {
  const [lots, setLots] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/b2b/lots/mine");
      setLots(r.data.lots || []);
      setByStatus(r.data.by_status || {});
    } catch {
      toast.error("Errore caricamento lotti");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const withdraw = async (id) => {
    if (!window.confirm("Ritirare questo lotto? Non sarà più visibile nel marketplace.")) return;
    try {
      await api.delete(`/b2b/lots/${id}`);
      toast.success("Lotto ritirato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <div data-testid="b2b-my-lots-page" className="container-xl py-10">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="flex items-center gap-3">
            <ListChecks className="h-6 w-6 text-[hsl(var(--accent))]" />
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] font-semibold">I miei lotti</div>
          </div>
          <h1 className="editorial-heading text-4xl md:text-5xl mt-1">Lista vini · food box in vendita</h1>
        </div>
        <Link to="/b2b/lots/sell" data-testid="my-lots-add-btn" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white font-semibold rounded-sm hover:bg-[hsl(var(--primary))]/90 transition-colors">
          <Plus className="h-4 w-4" /> Pubblica nuovo lotto
        </Link>
      </div>

      {/* STATUS SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-6">
        {Object.entries(STATUS_CONFIG).map(([k, cfg]) => (
          <div key={k} className={`px-4 py-3 border rounded-sm ${cfg.color}`} data-testid={`status-summary-${k}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{cfg.label}</div>
            <div className="text-2xl font-bold">{byStatus[k] || 0}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Caricamento…</div>
      ) : lots.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Package className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <div className="text-muted-foreground mb-4">Non hai ancora pubblicato lotti.</div>
          <Link to="/b2b/lots/sell" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white font-semibold rounded-sm">
            <Plus className="h-4 w-4" /> Pubblica il primo lotto
          </Link>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="my-lots-table">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-left px-4 py-3">Titolo</th>
                <th className="text-right px-4 py-3">Quantità</th>
                <th className="text-right px-4 py-3">Prezzo</th>
                <th className="text-right px-4 py-3">Valore</th>
                <th className="text-center px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {lots.map((lot) => {
                const cfg = STATUS_CONFIG[lot.status] || STATUS_CONFIG.pending_approval;
                return (
                  <tr key={lot.id} className="border-t border-border hover:bg-[hsl(var(--muted))]/30">
                    <td className="px-4 py-3">{lot.product_type === "wine" ? <Wine className="h-4 w-4 text-[#7A1C2B]" /> : <Package className="h-4 w-4 text-[#C9A961]" />}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{lot.title}</div>
                      <div className="text-xs text-muted-foreground">{new Date(lot.created_at).toLocaleDateString("it-IT")}</div>
                    </td>
                    <td className="px-4 py-3 text-right">{lot.quantity}</td>
                    <td className="px-4 py-3 text-right">€ {lot.price_per_unit?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold">€ {lot.total_price?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full border ${cfg.color}`}>{cfg.label}</span>
                      {lot.status === "rejected" && lot.admin_notes && (
                        <div className="text-[10px] text-red-700 mt-1 max-w-[200px]" title={lot.admin_notes}>{lot.admin_notes}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {["pending_approval", "approved"].includes(lot.status) && (
                        <button onClick={() => withdraw(lot.id)} data-testid={`withdraw-${lot.id}`} className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Ritira
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
